'use client';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useState } from 'react';
import { Lock, ExternalLink } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ui/components/ui/tooltip';
import { TopicItemMenu } from './topic-item-menu';
import { TopicDeleteModal } from './topic-delete-modal';

interface SortableItemProps {
  id: string;
  title?: string;
  description?: string;
  public?: boolean;
  isLink?: boolean;
  linkUrl?: string;
  onClick?: () => void;
  onEditLink?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDeleteTopic?: (id: string) => Promise<void>;
  isDeleting?: boolean;
  isDeleteSuccess?: boolean;
}

export const SortableItem: React.FC<SortableItemProps> = ({
  id,
  title = 'Untitled',
  description,
  public: isPublic = true,
  isLink = false,
  linkUrl,
  onClick,
  onEditLink,
  onDeleteTopic,
  isDeleting,
  isDeleteSuccess,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<string | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCardClick = () => {
    if (isLink && linkUrl) {
      // window.open(linkUrl, '_blank');
      window.location.href = linkUrl;
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={style}
        className={`border rounded-md p-4 shadow-sm hover:shadow transition cursor-pointer relative ${
          isLink ? 'min-h-[8rem] bg-blue-50' : 'min-h-[12rem] bg-white'
        }`}
        onClick={handleCardClick}>
        <div className="flex flex-row items-center justify-between">
          <h3 className="font-semibold text-base truncate">{title}</h3>
          <div className="flex items-center gap-2">
            {!isPublic && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Lock className="w-4 h-4 text-gray-500 flex-shrink-0 cursor-help" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Private Topic</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TopicItemMenu
              isOpen={isMenuOpen}
              onOpenChange={setMenuOpen}
              onEdit={isLink ? onEditLink : undefined}
              onDelete={(e) => {
                e.stopPropagation();
                setTopicToDelete(id);
                setOpenDeleteModal(true);
              }}
            />
          </div>
        </div>

        {description && <p className="text-sm text-gray-600 line-clamp-3 mt-1">{description}</p>}

        {isLink && (
          <div className="absolute bottom-4 right-4">
            <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
          </div>
        )}
      </div>

      <TopicDeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        topicId={topicToDelete}
        onDeleteTopic={onDeleteTopic}
        isDeleting={isDeleting}
        isDeleteSuccess={isDeleteSuccess}
      />
    </>
  );
};
